/**
 * CreditsPage — /account/credits (current store).
 *
 * The current store's client credits: what's available to spend, each credit's balance, brand and
 * expiration, and the orders it was used on. Credits belong to a store, so this shows the selected
 * store only (switch stores from the header to see another's). Reasons and voided credits are never
 * sent to the shop.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useStoreCredits } from "../../hooks/useStoreCredits";
import { creditLineLabel, creditScopeLabel, stashHouseBrandsText } from "../../services/creditService.js";
import { formatCurrency, formatDate } from "../../utils/formatters";

const STATUS = {
  available: { label: "Available", className: "bg-stash-accent" },
  applied: { label: "Applied", className: "bg-secondary" },
  expired: { label: "Expired", className: "bg-warning text-dark" },
};

// Server timestamps are UTC but arrive without a zone marker.
const formatUtcDate = (value) => {
  if (!value) return "";
  const hasZone = /Z$|[+-]\d{2}:\d{2}$/.test(value);
  return formatDate(new Date(hasZone ? value : `${value}Z`));
};

// Available = money left to spend; Applied = fully used on orders; Expired = past its date with money
// unused. Expiration is switched off for now (the API reports nothing as expired), so the Expired tab
// only shows when the store actually has an expired credit.
const TABS = {
  available: { label: "Available", matches: (c) => c.status === "available", empty: "No credits to spend right now." },
  applied: { label: "Applied", matches: (c) => c.status === "applied", empty: "No applied credits yet." },
  expired: { label: "Expired", matches: (c) => c.status === "expired", empty: "No expired credits.", onlyWhenAny: true },
  all: { label: "All", matches: () => true },
};

const isStashHouse = (credit) => creditLineLabel(credit) === "Stash House";

const CreditsPage = () => {
  const { enabled, availableTotal, credits, stashHouseBrands, storeName, isLoading, error } = useStoreCredits();
  const stashHouseNote = stashHouseBrandsText(stashHouseBrands);
  const [activeTab, setActiveTab] = useState("available");
  const shown = credits.filter(TABS[activeTab].matches);

  if (isLoading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading credits...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="row mb-4">
        <div className="col">
          <h1 className="mb-1">Credits</h1>
          <p className="text-muted mb-0">
            {storeName ? `Credits for ${storeName}` : "Credits for your store"}
          </p>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">We couldn&apos;t load your credits. Please try again.</div>
      )}

      {!error && !enabled && (
        <div className="alert alert-info">Credits aren&apos;t offered for this store.</div>
      )}

      {!error && enabled && (
        <>
          <div className="row mb-4">
            <div className="col-md-4">
              <div className="card border-stash-accent">
                <div className="card-body">
                  <div className="text-muted small">Available to spend</div>
                  <div className="fs-3 fw-bold text-stash-accent">{formatCurrency(availableTotal)}</div>
                </div>
              </div>
            </div>
          </div>

          <ul className="nav nav-tabs mb-3">
            {Object.entries(TABS)
              .filter(([, tab]) => !tab.onlyWhenAny || credits.some(tab.matches))
              .map(([key, tab]) => (
                <li className="nav-item" key={key}>
                  <button
                    type="button"
                    className={`nav-link ${activeTab === key ? "active" : ""}`}
                    onClick={() => setActiveTab(key)}
                  >
                    {tab.label}
                    <span className="badge bg-light text-dark ms-2">{credits.filter(tab.matches).length}</span>
                  </button>
                </li>
              ))}
          </ul>

          {shown.length === 0 ? (
            <div className="text-center text-muted py-5">
              <i className="bi bi-wallet2 display-4 d-block mb-3"></i>
              {credits.length === 0 ? "This store doesn't have any credits yet." : TABS[activeTab].empty}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th>Credit</th>
                    <th className="text-end">Amount</th>
                    <th className="text-end">Remaining</th>
                    <th>Date Created</th>
                    <th>Status</th>
                    <th>Used on</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((credit) => {
                    const status = STATUS[credit.status] ?? STATUS.available;
                    return (
                      <tr key={credit.id}>
                        <td>
                          <div className="fw-semibold">
                            {creditLineLabel(credit)}
                            {isStashHouse(credit) && stashHouseNote && (
                              <sup>
                                <a href="#stash-house-note" className="text-decoration-none ms-1">
                                  1
                                </a>
                              </sup>
                            )}
                          </div>
                          <div className="small text-muted">
                            {creditScopeLabel(credit)}
                            {credit.sourceOrderId && (
                              <>
                                {" · from "}
                                <Link to={`/orders?order=${credit.sourceOrderId}`}>order #{credit.sourceOrderId}</Link>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="text-end">{formatCurrency(credit.amount)}</td>
                        <td className="text-end fw-semibold">{formatCurrency(credit.remainingAmount)}</td>
                        <td>{formatUtcDate(credit.dateCreated)}</td>
                        <td>
                          <span className={`badge ${status.className}`}>{status.label}</span>
                        </td>
                        <td className="small">
                          {credit.uses.length === 0 ? (
                            <span className="text-muted">—</span>
                          ) : (
                            credit.uses.map((use) => (
                              <div key={`${use.orderId}-${use.dateApplied}`}>
                                <Link to={`/orders?order=${use.orderId}`} className="fw-semibold">
                                  Order #{use.legacyInvoice || use.orderId}
                                </Link>
                                {" · "}
                                {formatCurrency(use.amount)} on {formatUtcDate(use.dateApplied)}
                              </div>
                            ))
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {/* Footnote for the ¹ on Stash House credits: the house brands they can be used on. */}
              {stashHouseNote && shown.some(isStashHouse) && (
                <p id="stash-house-note" className="small text-muted mb-0">
                  <sup className="me-1">1</sup>
                  {stashHouseNote}
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CreditsPage;
